import {
  Button,
  DotsVerticalIcon,
  Dropdown,
  DropdownMenu,
  type DropdownMenuItem,
  type Row,
  Table,
  type TableColumns,
} from '@bp/ui-components';
import { type FragmentType, getFragmentData, graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { niceDate } from 'utils/dateCalculations';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { BpLink } from '../BpLink/BpLink';

const GroupWorkMaterialsTableFieldsDocument = graphql(`
  fragment GroupWorkMaterialsTableFields on Query {
    workMaterialsTable: workMaterials(where: { holder: { TeachingUnit: { group: { uuid: $groupUuid } } } }) {
      uuid
      __typename
      title
      created
      updated
      owner {
        uuid
        __typename
        displayName
      }
      material {
        uuid
        __typename
        fileEntries {
          uuid
          __typename
        }
      }
    }
  }
`);

type GroupWorkMaterialType = {
  uuid: string;
  title: string;
  filesCount: number;
  created: string;
  creator: string;
};

type GroupWorkMaterialsTableProps = {
  query: FragmentType<typeof GroupWorkMaterialsTableFieldsDocument> | undefined;
  groupUuid: string;
  onAdd: () => void;
  onEdit: (uuid: string) => void;
  onDelete: (uuid: string, title: string) => void;
  onShow: (uuid: string) => void;
};

export const GroupWorkMaterialsTable: FC<GroupWorkMaterialsTableProps> = ({
  groupUuid,
  query,
  onAdd,
  onEdit,
  onDelete,
  onShow,
}) => {
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();

  const fragment = getFragmentData(GroupWorkMaterialsTableFieldsDocument, query);
  const permissions = usePermissionChecker();
  const workMaterials = fragment?.workMaterialsTable;
  const data = workMaterials?.map((wm) => {
    return {
      uuid: wm.uuid,
      title: wm.title,
      created: niceDate(wm.updated, 'long', 'short') ?? '',
      filesCount: wm.material.fileEntries.length,
      creator: wm.owner.displayName ?? '',
    };
  });
  const { pimAuthClaims } = useAuthClaims();

  // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const memoizedColumns = useMemo((): TableColumns<GroupWorkMaterialType>[] => {
    return [
      {
        id: 'title',
        size: 450,
        header: t('common.name'),
        accessorKey: 'title',
        cell: ({ row }) => {
          return (
            <BpLink
              value={row.original.title}
              onNavigate={() => onShow(row.original.uuid)}
              isEditorOfCourse={
                // TODO: get Organization from group
                permissions?.isGroupEditor({
                  uuid: groupUuid,
                  organization: pimAuthClaims.getOrganization(),
                }) ?? false
              }
            />
          );
        },
      },
      {
        id: 'filesCount',
        size: 100,
        accessorKey: 'filesCount',
        header: t('common.documents'),
      },
      {
        id: 'created',
        size: 300,
        accessorKey: 'created',
        header: t('common.createdAt'),
      },
      {
        id: 'creator',
        size: 300,
        accessorKey: 'creator',
        header: t('common.createdBy'),
      },
    ];
  }, [groupUuid, permissions, pimAuthClaims, onShow]);

  const memoizedData = useMemo((): GroupWorkMaterialType[] => {
    return data ?? [];
  }, [data]);

  // biome-ignore lint/correctness/useExhaustiveDependencies(t): ignore t
  const memoizedDropdown = useCallback(
    (row: Row<GroupWorkMaterialType>): DropdownMenuItem[] => {
      return [
        {
          label: t('common.edit'),
          onClick: () => {
            onEdit(row.original.uuid);
          },
        },
        {
          label: t('messages.addFile'),
        },
        {
          type: 'ruler',
        },
        {
          color: 'error',
          label: t('delete.delete'),
          onClick: () => onDelete(row.original.uuid, row.original.title),
        },
      ];
    },
    [onDelete, onEdit],
  );

  const canEdit = groupUuid
    ? permissions?.canCreateBpGroupMaterial({
        uuid: groupUuid,
        organization: { uuid: pimAuthClaims.getOrganizationUuid() },
      })
    : false;

  return (
    <Table<GroupWorkMaterialType>
      data={memoizedData}
      columns={memoizedColumns}
      hideHeader={memoizedData.length === 0}
      showBorderRadius
      showShadow
      showActionBar
      isOnWhite={false}
      actionBarSettings={{
        showAddButton: canEdit,
      }}
      isDesktop={isDesktop}
      onAddClick={() => onAdd()}
      customLastColSpacing='var(--spacing-4)'
      emptyStateSettings={{
        title: t('workmaterials.noWorkmaterials'),
        subtitle: t('workmaterials.createHint'),
        padding: 'xl',
        hideIcon: true,
      }}
      lastCol={(row) => {
        return canEdit ? (
          <Dropdown noPadding trigger={<Button hierarchy='ghost' icon={<DotsVerticalIcon />} />}>
            <DropdownMenu data={memoizedDropdown(row)} />
          </Dropdown>
        ) : undefined;
      }}
    />
  );
};
